import { useContext, useRef, useEffect, useState } from "react";
import { Slider, Box } from "@mui/material";
import { FiltersContext } from "../store/filters-options-context";

export default function SliderRange({
    params,
    setParams,
    yearRange,
    isMobile
}) {

    const valuetext = (value) => {
        return `${marks[value]?.label}`;
    };

    const { yearValue, setYearValue, dataLoadingComplete } = useContext(FiltersContext);
    
    // State for protecting the API from excessive calls
    const [isDisabled, setIsDisabled] = useState(false);
    const [pendingChange, setPendingChange] = useState(null);
    const delayTimeoutRef = useRef(null);
    const disableTimeoutRef = useRef(null);
    const isUserInteractingRef = useRef(false);
    
    // Delay before applying changes (in milliseconds)
    const CHANGE_DELAY = 1000;
    // Disable duration after data loads (in milliseconds) 
    const DISABLE_DURATION = 2000;


    let marks = (yearRange || []).map((year, i) => ({
        value: i,
        label: year,
    }));

    const minDistance = 1;
    const maxDistance = 100;

    // Function to apply the actual parameter change
    const applyYearChange = (newValue) => {
        const filteredYears = (yearRange || []).filter(
            (year, index) => index >= newValue[0] && index <= newValue[1]
        );

        setParams((prevParams) => ({
            ...prevParams,
            year: [...filteredYears],
        }));
    };

    // Function to handle delayed change application
    const scheduleDelayedChange = (newValue) => {
        // Clear any existing timeout
        if (delayTimeoutRef.current) {
            clearTimeout(delayTimeoutRef.current);
        }

        // Store the pending change
        setPendingChange(newValue);

        // Set a new timeout to apply the change after delay
        delayTimeoutRef.current = setTimeout(() => {
            if (!isUserInteractingRef.current) {
                applyYearChange(newValue);
                setPendingChange(null);
            }
        }, CHANGE_DELAY);
    };

    const handleChange = (event, newValue, activeThumb) => {
        // Don't allow changes if slider is disabled
        if (isDisabled) {
            return;
        }

        if (!Array.isArray(newValue)) {
            return;
        }

        const distance = newValue[1] - newValue[0];

        // Adjust the distance if it exceeds the allowed range
        let adjustedValue = newValue;
        if (distance < minDistance) {
            if (activeThumb === 0) {
                const clamped = Math.min(newValue[0], newValue[1] - minDistance);
                adjustedValue = [clamped, clamped + minDistance];
            } else {
                const clamped = Math.max(newValue[1], newValue[0] + minDistance);
                adjustedValue = [clamped - minDistance, clamped];
            }
        } else if (distance > maxDistance) {
            if (activeThumb === 0) {
                const clamped = Math.min(newValue[0], newValue[1] - maxDistance);
                adjustedValue = [clamped, clamped + maxDistance];
            } else {
                const clamped = Math.max(newValue[1], newValue[0] + maxDistance);
                adjustedValue = [clamped - maxDistance, clamped];
            }
        }

        // Update the visual state immediately
        setYearValue(adjustedValue);
        
        // Mark that user is currently interacting
        isUserInteractingRef.current = true;
        
        // Schedule the delayed parameter change
        scheduleDelayedChange(adjustedValue);
    };

    const handleChangeCommitted = (event, newValue) => {
        // Mark that user has finished interacting
        isUserInteractingRef.current = false;
        
        // If there's a pending change, don't do anything here
        // The timeout will handle it
    };

    const handleMouseDown = () => {
        // User started interacting with slider
        isUserInteractingRef.current = true;
    };

    const handleMouseUp = () => {
        // User finished interacting with slider
        isUserInteractingRef.current = false;
    };

    // Clean up timeouts on component unmount
    useEffect(() => {
        return () => {
            const delayTimeout = delayTimeoutRef.current;
            const disableTimeout = disableTimeoutRef.current;
            
            if (delayTimeout) {
                clearTimeout(delayTimeout);
            }
            if (disableTimeout) {
                clearTimeout(disableTimeout);
            }
        };
    }, []);

    // Function to disable slider after data loading completes
    const disableTemporarily = () => {
        setIsDisabled(true);
        
        // Clear any existing disable timeout
        if (disableTimeoutRef.current) {
            clearTimeout(disableTimeoutRef.current);
        }
        
        disableTimeoutRef.current = setTimeout(() => {
            setIsDisabled(false);
        }, DISABLE_DURATION);
    };

    // Listen for data loading completion and temporarily disable slider
    useEffect(() => {
        if (dataLoadingComplete > 0) {
            disableTemporarily();
        }
    }, [dataLoadingComplete]);

    return (
        <Box
            display="flex"
            width="100%"
            sx={{ 
                alignItems: "center",
                opacity: isDisabled ? 0.6 : 1,
                pointerEvents: isDisabled ? "none" : "auto",
                position: "relative"
            }}
        >
            <Slider
                aria-labelledby="track-inverted-range-slider"
                max={marks.length - 1}
                step={1}
                marks={marks}
                onChange={handleChange}
                onChangeCommitted={handleChangeCommitted}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                disableSwap
                disabled={isDisabled}
                getAriaValueText={valuetext}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => (yearRange || [])[value]}
                value={yearValue}
                size="small"
                sx={{
                    flex: 1,
                    "& .MuiSlider-markLabel": {
                        fontSize: isMobile ? "7px" : "12px",
                        letterSpacing: isMobile ? '-0.3pt' : 'inherit',
                        transform: isMobile
                            ? "rotate(0) translateX(-50%)"
                            : "rotate(0) translateX(-50%)",
                        color: isDisabled ? "text.disabled" : "inherit"
                    },
                    "& .MuiSlider-track": {
                        backgroundColor: isDisabled ? "action.disabled" : "primary.main"
                    },
                    "& .MuiSlider-thumb": {
                        backgroundColor: isDisabled ? "action.disabled" : "primary.main"
                    }
                }}
            />
            {pendingChange && !isDisabled && (
                <Box
                    sx={{
                        position: "absolute",
                        top: -8,
                        right: 0,
                        fontSize: "10px",
                        color: "warning.main",
                        fontStyle: "italic",
                        pointerEvents: "none"
                    }}
                >
                    Applying changes...
                </Box>
            )}
        </Box>
    );
}
